'use client';

import { ProductCard } from '@/components/product-card';
import { useSellerProducts, type Product } from '@/context/product-context';
import { readSuggestedProducts, resolveSuggestedProducts } from '@/lib/suggested-products';
import { trailingSectionClasses, type SectionSeparator } from '@/lib/product-sections';
import { cn } from '@/lib/utils';

/**
 * The seller's own cross-sell row, under the reviews on a product page.
 *
 * WHY IT FETCHES ITS OWN PRODUCTS. `useSellerProducts` runs the scoped
 * `sellerId == X, status == 'live'` query, and that query IS the security
 * boundary for this feature: the stored ids are client-written, so resolving
 * them against anything wider would let a seller put somebody else's product,
 * or their own unpublished draft, under their page by pasting an id. Reading
 * the ids and reading the products in one component is what keeps those two
 * facts next to each other.
 *
 * Renders NOTHING at all when the seller has the section switched off, when
 * every id they picked has since stopped being live, or when the only id left
 * is this product itself. An empty heading under the reviews is worse than no
 * section, and a seller who archives the three products they were cross-selling
 * should not be left advertising a blank row.
 */
export function SuggestedProducts({
  sellerId,
  details,
  currentProductId,
  separator,
}: {
  sellerId: string | null | undefined;
  /** `products/{id}.details`, unvalidated. */
  details: unknown;
  currentProductId: string;
  /**
   * The seller's section separator, resolved by the page. Passed in rather than
   * re-resolved here so the row cannot disagree with the sections above it about
   * which mode the page is in.
   */
  separator: SectionSeparator;
}) {
  // Read the stored selection FIRST, and pass a null sellerId when there is
  // nothing to resolve. `useSellerProducts` short-circuits on a null id without
  // opening a listener, and that is the whole point: the section is off by
  // default, so subscribing unconditionally would put an extra snapshot
  // listener and a catalogue-sized read on EVERY product page view for a
  // feature most pages do not use. A hook cannot be called conditionally; its
  // argument can.
  const { enabled, productIds } = readSuggestedProducts(details);
  const wanted = enabled && productIds.length > 0;

  // Discounts on, matching every other grid on the site: a card here showing
  // the undiscounted price while the marketplace shows the sale price is the
  // kind of disagreement a buyer reads as a bait and switch.
  const { products } = useSellerProducts(wanted ? sellerId : null, { withDiscounts: true });
  const suggested = resolveSuggestedProducts(
    Array.from(products.values()),
    details,
    currentProductId,
  );

  return <SuggestedProductsRow products={suggested} separator={separator} />;
}

/**
 * The row itself, with no idea where its products came from.
 *
 * Split out because it has THREE render sites, the same three every other part
 * of a product page has: the live page above, and the two hand-rolled previews
 * inside the create and edit forms, which already hold the seller's live
 * products and their working selection and so must not open the query again.
 * The heading, the column count and the separator treatment are written once
 * here, so a seller's preview cannot show them a different row from the one
 * buyers get.
 *
 * Renders NOTHING for an empty list. An empty heading under the reviews is
 * worse than no section, and a seller who archives the three products they were
 * cross-selling should not be left advertising a blank row.
 */
export function SuggestedProductsRow({
  products,
  separator,
}: {
  products: readonly Product[];
  separator: SectionSeparator;
}) {
  if (products.length === 0) return null;

  return (
    <section className={cn("space-y-4", trailingSectionClasses(separator))}>
      {/* In the seller's heading font, like the page's other section headings;
          the cards' titles follow it through ProductCard. */}
      <h2 className="font-seller-heading">Suggested products</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            {...product}
            reviews={product.reviewCount ?? product.reviews?.length ?? 0}
          />
        ))}
      </div>
    </section>
  );
}
